/** Shared display helpers for Change Order pricing labels/values. */

import {
  formatCurrencyAbs,
  inferChangeOrderPricingType,
  type ChangeOrderPricingTextHints,
  type ChangeOrderPricingType,
} from './changeOrderPricing';

export function getCoPricingType(
  builderCost: number | null | undefined,
  clientPrice: number | null | undefined,
  text?: ChangeOrderPricingTextHints | null
): ChangeOrderPricingType {
  return inferChangeOrderPricingType(builderCost, clientPrice, text);
}

export function formatClientFacingPrice(
  clientPrice: number | null | undefined,
  text?: ChangeOrderPricingTextHints | null
): { isCredit: boolean; label: string; amountText: string } {
  const type = inferChangeOrderPricingType(null, clientPrice, text);
  const isCredit = type === 'credit';
  return {
    isCredit,
    label: isCredit ? 'Client Credit' : 'Client Price',
    amountText: clientPrice == null ? '—' : `$${formatCurrencyAbs(clientPrice)}`,
  };
}

/** Builder Cost and Client Price are independent commercial values.
 * Do not infer or display a Margin unless a future explicit margin field exists.
 * Compatibility keys remain null so older callers cannot accidentally derive one.
 */
export function formatInternalPricing(
  builderCost: number | null | undefined,
  clientPrice: number | null | undefined,
  text?: ChangeOrderPricingTextHints | null
) {
  const type = inferChangeOrderPricingType(builderCost, clientPrice, text);
  const isCredit = type === 'credit';
  return {
    type,
    isCredit,
    builderLabel: isCredit ? 'Builder Cost Reduction' : 'Builder Cost',
    clientLabel: isCredit ? 'Client Credit' : 'Client Price',
    marginLabel: null,
    builderText: builderCost == null ? null : `$${formatCurrencyAbs(builderCost)}`,
    clientText: clientPrice == null ? null : `$${formatCurrencyAbs(clientPrice)}`,
    marginText: null,
    marginValue: null,
    signedClient: clientPrice ?? 0,
    signedBuilder: builderCost ?? 0,
  };
}
